import { Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist/legacy/build/pdf';
import pdfWorkerUrl from 'pdfjs-dist/legacy/build/pdf.worker.min.js?url';

@Component({
  selector: 'app-pdf-thumbnail',
  standalone: false,
  template: `
    <div class="pdf-thumb" [style.width.px]="width">
      <div #container></div>
    </div>
  `,
  styles: [
    `
      .pdf-thumb canvas {
        width: 100%;
        height: auto;
        display: block;
      }
    `,
  ],
})
export class PdfThumbnailComponent implements OnChanges, OnDestroy {
  @Input() source!: string; // data URL, base64 or URL
  @Input() width = 260;
  @Input() renderAll = false;
  @ViewChild('container', { static: true }) containerRef!: ElementRef<HTMLDivElement>;

  private pdf: any = null;
  private destroyed = false;

  constructor() {
    try {
      // Use the worker URL emitted by the bundler/dev server. This avoids runtime version mismatches.
      try {
        (GlobalWorkerOptions as any).workerSrc = pdfWorkerUrl as any;
      } catch {
        // fallback: local asset
        try {
          (GlobalWorkerOptions as any).workerSrc = '/assets/pdf.worker.min.js';
        } catch {}
      }
    } catch {
      // fallback: rely on global worker path if configured
    }
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['source'] && this.source) {
      await this.renderPages(this.source);
    }
    if (changes['renderAll'] && !changes['source']) {
      // re-render when renderAll toggles
      await this.renderPages(this.source);
    }
  }

  // Render first page or all pages depending on `renderAll` input
  async renderPages(src: string) {
    // clear previous canvases
    try {
      this.clearContainer();
    } catch {}
    if (this.destroyed) return;
    try {
      const srcObj = this.getSrcObject(src);
      const loadingTask = getDocument(srcObj as any);
      this.pdf = await loadingTask.promise;
      const numPages = this.pdf.numPages || 0;

      const pagesToRender = this.renderAll ? Array.from({ length: numPages }, (_, i) => i + 1) : [1];

      for (const pageNum of pagesToRender) {
        const page = await this.pdf.getPage(pageNum);
        // Compute scale to fit requested logical width and increase resolution for DPR
        const initialViewport = page.getViewport({ scale: 1 });
        const logicalScale = this.width / initialViewport.width; // CSS scale to fit
        const dpr = Math.max(1, (typeof window !== 'undefined' && (window as any).devicePixelRatio) || 1);
        const renderScale = logicalScale * dpr; // high-res scale for crisp rendering
        const scaledViewport = page.getViewport({ scale: renderScale });

        // create canvas per page
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;
        canvas.width = Math.round(scaledViewport.width);
        canvas.height = Math.round(scaledViewport.height);
        canvas.style.width = Math.round(scaledViewport.width / dpr) + 'px';
        canvas.style.height = Math.round(scaledViewport.height / dpr) + 'px';
        canvas.style.display = 'block';
        canvas.style.marginBottom = '12px';

        const container = this.containerRef?.nativeElement;
        if (container) container.appendChild(canvas);

        const renderContext = {
          canvasContext: ctx,
          viewport: scaledViewport,
        };
        await page.render(renderContext).promise;
      }
    } catch (err) {
      // render error, leave blank
      console.error('[PdfThumbnail] render failed', err);
    }
  }

  private getSrcObject(src: string) {
    if (!src) return { url: '' };
    if (src.startsWith('data:application/pdf;base64,')) {
      const b64 = src.split(',', 2)[1];
      const arr = this.base64ToUint8Array(b64);
      return { data: arr };
    }
    // plain base64 detection (naive): long string of base64 chars
    if (/^[A-Za-z0-9+/=\s]+$/.test(src) && src.length > 1000) {
      const arr = this.base64ToUint8Array(src.replace(/\s+/g, ''));
      return { data: arr };
    }
    return { url: src };
  }

  private base64ToUint8Array(b64: string) {
    const bin = atob(b64);
    const len = bin.length;
    const u8 = new Uint8Array(len);
    for (let i = 0; i < len; i++) u8[i] = bin.charCodeAt(i);
    return u8;
  }

  private clearContainer() {
    try {
      const container = this.containerRef?.nativeElement;
      if (container) {
        while (container.firstChild) container.removeChild(container.firstChild);
      }
    } catch {
      /* ignore */
    }
  }

  ngOnDestroy() {
    this.destroyed = true;
    try {
      if (this.pdf) this.pdf.destroy();
    } catch {}
    try {
      this.clearContainer();
    } catch {}
  }
}
